/**
 * BOQ Service
 * Frontend API wrapper for BOQ / Materials Schedule endpoints
 */

import api from './api';

const boqService = {
  // ─── Documents ───

  createDocument(data) {
    return api.post('/boq/documents', data).then(r => r.data);
  },

  draftWithAI(projectId) {
    return api.post(`/boq/projects/${projectId}/ai-draft`, {}, { timeout: 60000 }).then(r => r.data);
  },

  getProjectBOQs(projectId, params = {}) {
    return api.get(`/boq/projects/${projectId}/documents`, { params }).then(r => r.data);
  },

  getProjectStages(projectId) {
    return api.get(`/projects/${projectId}/milestones`).then(r => r.data);
  },

  getDocument(boqId) {
    return api.get(`/boq/documents/${boqId}`).then(r => r.data);
  },

  updateDocument(boqId, data) {
    return api.put(`/boq/documents/${boqId}`, data).then(r => r.data);
  },

  acknowledgeDocument(boqId) {
    return api.post(`/boq/documents/${boqId}/acknowledge`).then(r => r.data);
  },

  // ─── Categories ───

  addCategory(boqId, data) {
    return api.post(`/boq/documents/${boqId}/categories`, data).then(r => r.data);
  },

  updateCategory(categoryId, data) {
    return api.put(`/boq/categories/${categoryId}`, data).then(r => r.data);
  },

  deleteCategory(categoryId) {
    return api.delete(`/boq/categories/${categoryId}`).then(r => r.data);
  },

  // ─── Items ───

  addItem(categoryId, data) {
    return api.post(`/boq/categories/${categoryId}/items`, data).then(r => r.data);
  },

  updateItem(itemId, data) {
    return api.put(`/boq/items/${itemId}`, data).then(r => r.data);
  },

  deleteItem(itemId) {
    return api.delete(`/boq/items/${itemId}`).then(r => r.data);
  },

  // ─── Purchases ───

  logPurchase(itemId, data) {
    return api.post(`/boq/items/${itemId}/purchases`, data).then(r => r.data);
  },

  getItemPurchases(itemId) {
    return api.get(`/boq/items/${itemId}/purchases`).then(r => r.data);
  },

  getDocumentPurchases(boqId) {
    return api.get(`/boq/documents/${boqId}/purchases`).then(r => r.data);
  },

  // ─── Labour Payments ───

  logLaborPayment(categoryId, data) {
    return api.post(`/boq/categories/${categoryId}/labor-payments`, data).then(r => r.data);
  },

  getCategoryLaborPayments(categoryId) {
    return api.get(`/boq/categories/${categoryId}/labor-payments`).then(r => r.data);
  },

  getDocumentLaborPayments(boqId) {
    return api.get(`/boq/documents/${boqId}/labor-payments`).then(r => r.data);
  },

  // ─── Changes ───

  getPendingChanges(boqId) {
    return api.get(`/boq/documents/${boqId}/changes`).then(r => r.data);
  },

  acknowledgeChanges(changeIds) {
    return api.post('/boq/changes/acknowledge', { change_ids: changeIds }).then(r => r.data);
  },

  rejectChange(changeId, reason) {
    return api.post(`/boq/changes/${changeId}/reject`, { reason }).then(r => r.data);
  },

  // ─── Comments ───

  addComment(boqId, data) {
    return api.post(`/boq/documents/${boqId}/comments`, data).then(r => r.data);
  },

  getComments(boqId, targetType, targetId) {
    const params = {};
    if (targetType) params.target_type = targetType;
    if (targetId) params.target_id = targetId;
    return api.get(`/boq/documents/${boqId}/comments`, { params }).then(r => r.data);
  },

  // ─── Summary ───

  getSummary(boqId) {
    return api.get(`/boq/documents/${boqId}/summary`).then(r => r.data);
  },

  // ─── Integration ───

  getUnifiedSummary(boqId) {
    return api.get(`/boq/documents/${boqId}/unified-summary`).then(r => r.data);
  },

  getUnmatchedItems(boqId) {
    return api.get(`/boq/documents/${boqId}/unmatched-items`).then(r => r.data);
  },

  linkOrderItem(data) {
    return api.post('/boq/links', data).then(r => r.data);
  },

  unlinkOrderItem(linkId) {
    return api.delete(`/boq/links/${linkId}`).then(r => r.data);
  }
};

export default boqService;
